import React from 'react';
import axios from 'axios';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Tooltip,
  Typography,
  TextField,
} from '@mui/material';

import SaveIcon from '@mui/icons-material/Save';
import PollIcon from '@mui/icons-material/Poll';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import BarChartIcon from '@mui/icons-material/BarChart';
import DonutLargeIcon from '@mui/icons-material/DonutLarge';
import StackedBarChartIcon from '@mui/icons-material/StackedBarChart';
import IosShareIcon from '@mui/icons-material/IosShare';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DeleteIcon from '@mui/icons-material/Delete';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Analytics, Comment } from '@mui/icons-material';

import BasicTable2 from '../tools/BasicTable2';
import ImageEditor from '../tools/ImageEditor';
import MessageDialog from '../tools/MessageDialog';
import * as ETVConstants from '../ETVConstants';
import SelectAvailableScreensNew from './SelectAvailableScreensNew';

import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip as ReTooltip,
  CartesianGrid,
  LabelList,
  PieChart,
  Pie,
  Legend,
  Cell,
} from 'recharts';

const COLORS = ['#0072B2', '#E69F00', '#009E73', '#D55E00', '#CC79A7', '#56B4E9'];

class SurveyMgmt extends React.Component {
  constructor(props) {
    super(props);
    this.analysisRef = React.createRef();

    this.receiveImage = this.receiveImage.bind(this);
    this.selectedScreens = this.selectedScreens.bind(this);
    this.closeScreenSelection = this.closeScreenSelection.bind(this);
  }

  state = {
    // data
    items: [],
    locations: [],
    data: undefined,

    // ui
    window: '',
    isLoading: false,

    // message dialog
    info_type: 'info',
    info_message: '',
    info_title: '',
    spinning: false,

    // creation
    survey_type: 0, // 0 satisfaction, 1 general
    selected_channel: 0,
    selected_screens: [],
    selected_language: 'all',
    lang: 'en',

    new_question: '',
    ans_1: '',
    ans_2: '',
    ans_3: '',
    ans_4: '',
    ans_5: '',

    // image flow
    survey_img: undefined, // dataURL from ImageEditor
    content_ratio: 1,
    uploaded_filename: undefined,
    uploaded_filesize: 0,

    // analysis / comments
    chart_mode: 'bar',
    cur_survey_id: undefined,
    question: '',
    survey_comments: [],
  };

  componentDidMount() {
    this.loadItems();
  }

  // ---------- helpers ----------
  getItem(id) {
    return (this.state.items || []).find((it) => it.id === id);
  }

  getChartRows() {
    const rows = this.state.data?.slice(1) || [];
    return rows.map(([name, hits]) => ({ name: String(name ?? ''), hits: Number(hits ?? 0) }));
  }

  getTotalHits() {
    return this.getChartRows().reduce((sum, r) => sum + (r.hits || 0), 0);
  }

  getDataMax() {
    return this.getChartRows().reduce((m, r) => Math.max(m, Number(r.hits || 0)), 0);
  }

  getLeader() {
    const rows = this.getChartRows();
    if (!rows.length) return null;
    return rows.reduce((max, r) => (r.hits > max.hits ? r : max), rows[0]);
  }

  toggleChartMode() {
    this.setState((s) => ({ chart_mode: s.chart_mode === 'bar' ? 'pie' : 'bar' }));
  }

  // ---------- load ----------
  loadItems() {
    const accessToken = localStorage.getItem('etv.access_token');
    const reqUserID = localStorage.getItem('etv.user_id');

    let url = `${ETVConstants.getServerBase()}/survey_mgmt?user_id=${this.props.user_id}`;
    if (this.state.selected_language && this.state.selected_language !== 'all') {
      url += `&country=${encodeURIComponent(this.state.selected_language)}`;
    }

    this.setState({ isLoading: true });

    fetch(url, { headers: { user_id: reqUserID, access_token: accessToken } })
      .then((res) => res.json())
      .then(
        (result) => {
          const surveys = result?.surveys || [];
          const locations = result?.locations || [];

          let data = this.state.data;
          if (this.state.cur_survey_id != null) {
            const cur = surveys.find((s) => s.id === this.state.cur_survey_id);
            data = cur ? this.extractSurveyDataFromItem(cur) : data;
          }

          this.setState({
            items: surveys,
            locations,
            data,
            isLoading: false,
          });
        },
        (error) => {
          this.setState({ items: [], locations: [], data: undefined, isLoading: false });
          this.showErrorMessage(ETVConstants.trans('error') || 'error', String(error));
        }
      );
  }

  // ---------- dialogs ----------
  showWindow(divID, show) {
    this.setState({ window: show ? divID : '' });
  }

  showInfoMessage(title, msg, isSpinning) {
    this.setState({
      window: 'message_div',
      info_type: 'info',
      info_title: title,
      info_message: msg,
      spinning: !!isSpinning,
    });
  }

  showSuccessMessage(title, msg) {
    this.setState({
      window: 'message_div',
      info_type: 'success',
      info_title: title,
      info_message: msg,
      spinning: false,
    });
  }

  showErrorMessage(title, msg) {
    this.setState({
      window: 'message_div',
      info_type: 'error',
      info_title: title,
      info_message: msg,
      spinning: false,
    });
  }

  closeInfoMessage() {
    this.setState({ window: '', info_type: '', info_title: '', info_message: '', spinning: false });
  }

  // ---------- actions ----------
  cmdSubmit(url, successTxt, failText) {
    const accessToken = localStorage.getItem('etv.access_token');
    const reqUserID = localStorage.getItem('etv.user_id');

    fetch(url, { headers: { user_id: reqUserID, access_token: accessToken } })
      .then((res) => res.json())
      .then(
        (result) => {
          if (result?.result) {
            this.showSuccessMessage(ETVConstants.trans('success') || 'success', successTxt);
            this.loadItems();
          } else {
            this.showErrorMessage(ETVConstants.trans('error') || 'error', failText);
          }
        },
        (error) => this.showErrorMessage(ETVConstants.trans('error') || 'error', String(error))
      );
  }

  resetSurvey(surveyID) {
    const url = `${ETVConstants.getServerBase()}/survey_mgmt?action=reset&survey_id=${encodeURIComponent(String(surveyID))}`;
    this.cmdSubmit(url, ETVConstants.trans('survey_resetted') || 'survey resetted', ETVConstants.trans('failure') || 'failure');
  }

  deleteSurvey(surveyID) {
    const url = `${ETVConstants.getServerBase()}/survey_mgmt?action=delete&survey_id=${encodeURIComponent(String(surveyID))}`;
    this.cmdSubmit(url, ETVConstants.trans('survey_deleted') || 'survey deleted', ETVConstants.trans('failure') || 'failure');
  }

  // ---------- create survey flow ----------
  createSatisfactionSurvey() {
    this.setState({
      survey_type: 0,
      ans_1: 'excellent',
      ans_2: 'good',
      ans_3: 'average',
      ans_4: 'poor',
      ans_5: 'very_poor',
      new_question: '',
      selected_channel: 0,
      selected_screens: [],
      survey_img: undefined,
      uploaded_filename: undefined,
      uploaded_filesize: 0,
    });
    this.showWindow('create_satisfaction_survey_div', true);
  }

  createGeneralSurvey() {
    this.setState({
      survey_type: 1,
      ans_1: '',
      ans_2: '',
      ans_3: '',
      ans_4: '',
      ans_5: '',
      new_question: '',
      selected_channel: 0,
      selected_screens: [],
      survey_img: undefined,
      uploaded_filename: undefined,
      uploaded_filesize: 0,
    });
    this.showWindow('create_general_survey_div', true);
  }

  selectedScreens(screenIDs) {
    this.setState({ selected_screens: screenIDs || [] }, () => {
      if (this.state.survey_type === 0) this.showWindow('create_satisfaction_survey_div', true);
      else this.showWindow('create_general_survey_div', true);
    });
  }

  closeScreenSelection() {
    if (this.state.survey_type === 0) this.showWindow('create_satisfaction_survey_div', true);
    else this.showWindow('create_general_survey_div', true);
  }

  receiveImage(imgDataUrl, contentRatio) {
    this.setState({ survey_img: imgDataUrl, content_ratio: contentRatio || 1 });
  }

  base64ToFile(dataUrl, filename) {
    const arr = dataUrl.split(',');
    const mime = arr[0].match(/:(.*?);/)?.[1] || 'image/png';
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) u8arr[n] = bstr.charCodeAt(n);
    return new File([u8arr], filename, { type: mime });
  }

  getMB(len) {
    const num = len / 1024 / 1024;
    const mb = Math.round(num * 100) / 100;
    return `${mb}MB`;
  }

  showPercentage(loaded, total) {
    if (!total) return '0%';
    const percent = Math.min(100, (loaded / total) * 100);
    return `${percent.toFixed(0)}%`;
  }

  saveSurveyWithImage = () => {
    if (!this.state.survey_img) {
      this.showErrorMessage(ETVConstants.trans('error') || 'error', 'no image yet');
      return;
    }
    const today = new Date();
    const dateStr = today.toISOString().slice(0, 10);
    const file = this.base64ToFile(this.state.survey_img,`survey_${dateStr}.png`);
    this.uploadFileToGCS(file, this.state.content_ratio);
  };

  uploadFileToGCS(file, contentRatio) {
    this.showInfoMessage(ETVConstants.trans('upload') || 'upload', `(${this.getMB(file.size)})`, true);

    const accessToken = localStorage.getItem('etv.access_token');
    const reqUserID = localStorage.getItem('etv.user_id');
    const userID = this.props.user_id;

    const target_dir = `/${userID}/content_library`;
    const myRand = Math.floor(Math.random() * 100000 + 1);
    const filename = `${myRand}_${file.name}`;

    const url = `${ETVConstants.getServerBase()}/content_library?action=request_upload&user_id=${encodeURIComponent(
      String(userID)
    )}&target_dir=${encodeURIComponent(target_dir)}&filename=${encodeURIComponent(filename)}`;

    fetch(url, { headers: { user_id: reqUserID, access_token: accessToken } })
      .then((res) => res.json())
      .then(
        (result) => {
          if (!result?.url || !result?.fields) {
            this.showErrorMessage(ETVConstants.trans('error') || 'error', 'upload init failed');
            return;
          }
          const formData = new FormData();
          Object.entries(result.fields).forEach(([key, value]) => formData.append(key, value));
          formData.append('file', file);

          const config = {
            headers: { 'content-type': 'multipart/form-data' },
            onUploadProgress: (progressEvent) =>
              this.showInfoMessage(
                ETVConstants.trans('upload') || 'upload',
                `(${this.showPercentage(progressEvent.loaded, file.size)})`,
                true
              ),
          };

          axios
            .post(result.url, formData, config)
            .then(() => {
              this.setState(
                {
                  uploaded_filename: filename,
                  uploaded_filesize: file.size,
                  content_ratio: contentRatio || 1,
                },
                () => {
                  this.showInfoMessage(ETVConstants.trans('upload') || 'upload', ETVConstants.trans('register') || 'register', true);
                  this.registerNewSurvey();
                }
              );
            })
            .catch((error) => {
              this.showErrorMessage(ETVConstants.trans('error') || 'error', String(error));
            });
        },
        (error) => this.showErrorMessage(ETVConstants.trans('error') || 'error', String(error))
      );
  }

  registerNewSurvey() {
    const accessToken = localStorage.getItem('etv.access_token');
    const reqUserID = localStorage.getItem('etv.user_id');
    const {
      new_question,
      ans_1,
      ans_2,
      ans_3,
      ans_4,
      ans_5,
      survey_type,
      selected_channel,
      selected_screens,
      uploaded_filename,
      uploaded_filesize,
      content_ratio,
    } = this.state;

    if (!new_question || !new_question.trim()) {
      this.showErrorMessage(ETVConstants.trans('error') || 'error', 'question is empty');
      return;
    }
    if (!uploaded_filename) {
      this.showErrorMessage(ETVConstants.trans('error') || 'error', 'image not uploaded yet');
      return;
    }

    this.showInfoMessage(
      ETVConstants.trans('survey') || 'survey',
      ETVConstants.trans('creating_survey') || 'creating survey',
      true
    );

    let url = `${ETVConstants.getServerBase()}/survey_mgmt?action=create_survey`;
    url += `&survey_type=${encodeURIComponent(String(survey_type))}`;
    url += `&question=${encodeURIComponent(new_question)}`;
    url += `&user_id=${encodeURIComponent(String(reqUserID))}`;

    if (ans_1) url += `&ans_1=${encodeURIComponent(ans_1)}`;
    if (ans_2) url += `&ans_2=${encodeURIComponent(ans_2)}`;
    if (ans_3) url += `&ans_3=${encodeURIComponent(ans_3)}`;
    if (ans_4) url += `&ans_4=${encodeURIComponent(ans_4)}`;
    if (ans_5) url += `&ans_5=${encodeURIComponent(ans_5)}`;

    url += `&channel=${encodeURIComponent(String(selected_channel))}`;
    (selected_screens || []).forEach((id) => (url += `&device_id=${encodeURIComponent(String(id))}`));

    const target_dir = `/${reqUserID}/content_library`;
    const virtual_dir = `/`;

    url += `&target_dir=${encodeURIComponent(target_dir)}`;
    url += `&virtual_dir=${encodeURIComponent(virtual_dir)}`;
    url += `&content_ratio=${encodeURIComponent(String(content_ratio || 1))}`;
    url += `&file_size=${encodeURIComponent(String(uploaded_filesize || 0))}`;
    url += `&filename=${encodeURIComponent(String(uploaded_filename))}`;

    fetch(url, { headers: { user_id: reqUserID, access_token: accessToken } })
      .then(async (res) => {
        const raw = await res.text();

        let data = null;
        try {
          data = raw ? JSON.parse(raw) : null;
        } catch {}

        if (!res.ok) {
          const msg = data?.message || data?.error || data?.reason || raw || `HTTP ${res.status}`;
          throw new Error(String(msg));
        }
        return data;
      })
      .then(() => {
        this.showSuccessMessage(
          ETVConstants.trans('survey') || 'survey',
          ETVConstants.trans('survey_registered') || 'survey registered'
        );

        this.setState({
          new_question: '',
          ans_1: '',
          ans_2: '',
          ans_3: '',
          ans_4: '',
          ans_5: '',
          selected_channel: 0,
          selected_screens: [],
          survey_img: undefined,
          uploaded_filename: undefined,
          uploaded_filesize: 0,
        });
        this.showWindow('create_satisfaction_survey_div', false);
        this.showWindow('create_general_survey_div', false);
        this.loadItems();
      })
      .catch((err) => {     
        this.showErrorMessage(ETVConstants.trans('error') || 'error', String(err?.message || err));
      });
  }

  // ---------- analysis / comments ----------
  extractSurveyDataFromItem(item) {
    const data = [];
    data[0] = ['Answer', 'Hits'];
    const answers = item?.ans || [];
    for (let i = 0; i < answers.length; i++) {
      const answer = String(answers[i]?.answer ?? '');
      const hits = Number(answers[i]?.hits ?? 0);
      data[i + 1] = [answer, hits];
    }
    return data;
  }

  analyzeSurvey(id) {
    const item = this.getItem(id);
    if (!item) return;
    const data = this.extractSurveyDataFromItem(item);
    this.setState({ cur_survey_id: id, question: item.question, data, chart_mode: 'bar' });
    this.showWindow('analyze_survey_div', true);
  }

  showComments(id) {
    const item = this.getItem(id);
    if (!item) return;

    const comments = (item.comments || []).filter((c) => typeof c === 'string' && c.trim().length > 0);
    const ordered = comments.slice().reverse();

    this.setState({ cur_survey_id: id, question: item.question, survey_comments: ordered });
    this.showWindow('comments_div', true);
  }

  createLanguageDropDown() {
    const languageNames = new Intl.DisplayNames([this.state.lang || 'en'], { type: 'language' });

    const formatLanguage = (code) => {
      try {
        const lang = String(code || '').split('-')[0];
        return languageNames.of(lang) || code;
      } catch {
        return code;
      }
    };

    return (
      <FormControl size="small" sx={{ minWidth: 260 }}>
        <InputLabel>{ETVConstants.trans('select_replies_by_language')}</InputLabel>
        <Select
          label={ETVConstants.trans('select_replies_by_language')}
          value={this.state.selected_language}
          onChange={(e) => this.setState({ selected_language: e.target.value }, () => this.loadItems())}
        >
          <MenuItem value="all">{ETVConstants.trans('all')}</MenuItem>
          {(this.state.locations || []).map((code) => (
            <MenuItem key={`lang_${code}`} value={code}>
              {formatLanguage(code)}
              <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                ({code})
              </Typography>
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  }

  async exportAnalysisAsPNG() {
    if (!this.analysisRef.current) return;
    const canvas = await html2canvas(this.analysisRef.current, { scale: 2, useCORS: true });
    const dataUrl = canvas.toDataURL('image/png', 1.0);
    const a = document.createElement('a');
    a.href = dataUrl;
    a.download = `survey-analysis-${this.state.cur_survey_id || 'export'}.png`;
    a.click();
  }

  async exportAnalysisAsPDF() {
    if (!this.analysisRef.current) return;
    const canvas = await html2canvas(this.analysisRef.current, { scale: 2, useCORS: true });
    const imgData = canvas.toDataURL('image/png', 1.0);

    const pdf = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const imgWidth = pageWidth - 60;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    const y = (pageHeight - imgHeight) / 2;

    pdf.addImage(imgData, 'PNG', 30, y, imgWidth, imgHeight);
    pdf.save(`survey-analysis-${this.state.cur_survey_id || 'export'}.pdf`);
  }

  renderImageEditorBlock() {
    return (
      <Stack spacing={2}>
        <ImageEditor onChange={this.receiveImage} />
      </Stack>
    );
  }

  render() {
    const columnsData = [
      {
        title: ETVConstants.trans('question'),
        field: 'question',
        render: (rowData) => (
          <Box sx={{ minWidth: 350, pl: 1, pr: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {rowData.question}
            </Typography>
          </Box>
        ),
      },
      { title: ETVConstants.trans('hits'), field: 'total_hits' },
      {
        title: ETVConstants.trans('analyze'),
        field: '',
        render: (rowData) => (
          <Button size="small" startIcon={<Analytics />} variant="contained" onClick={() => this.analyzeSurvey(rowData.id)}>
            {ETVConstants.trans('analyze')}
          </Button>
        ),
      },
      {
        title: ETVConstants.trans('comments'),
        field: '',
        render: (rowData) => (
          <Button size="small" startIcon={<Comment />} variant="outlined" onClick={() => this.showComments(rowData.id)}>
            {ETVConstants.trans('comments')}
          </Button>
        ),
      },
      {
        title: ETVConstants.trans('reset'),
        field: '',
        render: (rowData) => (
          <Button
            size="small"
            startIcon={<RestartAltIcon />}
            variant="outlined"
            color="warning"
            onClick={() => this.resetSurvey(rowData.id)}
          >
            {ETVConstants.trans('reset')}
          </Button>
        ),
      },
      {
        title: ETVConstants.trans('delete'),
        field: 'select_layout',
        render: (rowData) => (
          <Tooltip title={ETVConstants.trans('delete')}>
            <IconButton size="small" color="error" onClick={() => this.deleteSurvey(rowData.id)}>
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        ),
      },
    ];

    const chartRows = this.getChartRows();
    const totalHits = this.getTotalHits();
    const dataMax = this.getDataMax();
    const chartRowsEps = chartRows.map((r) => ({ ...r, hits: Number(r.hits || 0) }));

    const canSaveGeneral =
      this.state.new_question.trim().length > 0 &&
      this.state.ans_1.trim().length > 0 &&
      this.state.ans_2.trim().length > 0 &&
      !!this.state.survey_img;

    const canSaveSatisfaction = this.state.new_question.trim().length > 0 && !!this.state.survey_img;

    return (
      <Box maxWidth="lg" sx={{ py: 3 }}>
        {/* Header */}
        <Stack direction="row" alignItems="left" justifyContent="space-between" sx={{ mb: 2 }}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <BarChartIcon />
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              {ETVConstants.trans('surveys_management') || 'surveys management'}
            </Typography>
          </Stack>
          <Stack direction="row" spacing={1}>
            <Button variant="contained" startIcon={<PollIcon />} onClick={() => this.createSatisfactionSurvey()}>
              {ETVConstants.trans('create_satisfaction_survey')}
            </Button>
            <Button variant="outlined" startIcon={<PollIcon />} onClick={() => this.createGeneralSurvey()}>
              {ETVConstants.trans('create_general_survey')}
            </Button>
          </Stack>
        </Stack>

        {/* Table Card */}
        <Card sx={{ borderRadius: 3 }}>
          <CardHeader title={ETVConstants.trans('surveys_list') || 'surveys'} subheader={ETVConstants.trans('latest_results') || 'latest results'} />
          <Divider />
          <CardContent>
            {this.state.items && this.state.items.length > 0 ? (
              <BasicTable2 data={this.state.items} columns={columnsData} title="" pageSize={10} allow_search={false} />
            ) : (
              <Stack alignItems="center" spacing={1} sx={{ py: 6, color: 'text.secondary' }}>
                <InfoOutlinedIcon />
                <Typography variant="body2">{ETVConstants.trans('no_surveys_yet') || 'no surveys yet'}</Typography>
              </Stack>
            )}
          </CardContent>
        </Card>

        {/* Message dialog */}
        <MessageDialog
          spinning={this.state.spinning}
          type={this.state.info_type}
          open={this.state.window === 'message_div'}
          onClose={() => this.closeInfoMessage()}
          title={this.state.info_title}
          message={this.state.info_message}
        />

        {/* Comments dialog */}
        <Dialog
          open={this.state.window === 'comments_div'}
          maxWidth="md"
          fullWidth
          onClose={() => this.setState({ window: '' })}
          PaperProps={{ sx: { borderRadius: 3 } }}
        >
          <DialogTitle sx={{ fontWeight: 700 }}>
            {ETVConstants.trans('comments') || 'comments'}{' '}
            <Typography component="span" variant="body2" color="text.secondary">
              ({(this.state.survey_comments || []).length})
            </Typography>
          </DialogTitle>

          <DialogContent dividers sx={{ p: 0 }}>
            {!this.state.survey_comments || this.state.survey_comments.length === 0 ? (
              <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
                  {ETVConstants.trans('no_comments_yet') || 'no comments yet'}
                </Typography>
                <Typography variant="body2">
                  {ETVConstants.trans('comments_will_appear_here') || 'comments will appear here once users submit them.'}
                </Typography>
              </Box>
            ) : (
              <Box sx={{ maxHeight: '65vh', overflow: 'auto' }}>
                <Stack divider={<Divider />} sx={{ p: 0 }}>
                  {(this.state.survey_comments || []).map((text, idx) => (
                    <Box key={`comment_${idx}`} sx={{ p: 2.5 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                        {(ETVConstants.trans('comment') || 'comment')} #{idx + 1}
                      </Typography>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
                        {text}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              </Box>
            )}
          </DialogContent>

          <DialogActions sx={{ px: 2, py: 1.5 }}>
            <Button onClick={() => this.setState({ window: '' })}>{ETVConstants.trans('close') || 'close'}</Button>
          </DialogActions>
        </Dialog>

        {/* Create Satisfaction Survey */}
        <Dialog
          open={this.state.window === 'create_satisfaction_survey_div'}
          keepMounted
          maxWidth="xl"
          fullWidth
          onClose={() => {}}
          PaperProps={{ sx: { borderRadius: 3 } }}
        >
          <DialogTitle>{ETVConstants.trans('create_new_satisfaction_survey') || 'create new satisfaction survey'}</DialogTitle>
            <DialogContent dividers>
              <Grid container spacing={3} sx={{ mt: 0.5 }}>
                {/* LEFT: Image editor (2/3) */}
                <Grid item xs={12} md={8}>
                  {this.renderImageEditorBlock()}
                </Grid>

                {/* RIGHT: Survey settings (1/3) */}
                <Grid item xs={12} md={4}>
                  <Stack spacing={2}>
                    <TextField
                      required
                      label={ETVConstants.trans('survey_question') || 'survey question'}
                      fullWidth
                      value={this.state.new_question}
                      onChange={(e) => this.setState({ new_question: e.target.value })}
                    />

                    <Button
                      variant="contained"
                      onClick={() => this.showWindow('select_screens_div', true)}
                    >
                      {ETVConstants.trans('select_screens') || 'select screens'} (
                      {this.state.selected_screens?.length || 0})
                    </Button>

                    <FormControl fullWidth>
                      <InputLabel>{ETVConstants.trans('channel') || 'channel'}</InputLabel>
                      <Select
                        label={ETVConstants.trans('channel') || 'channel'}
                        value={this.state.selected_channel}
                        onChange={(e) => this.setState({ selected_channel: e.target.value })}
                      >
                        <MenuItem value={0}>Channel 0</MenuItem>
                        <MenuItem value={1}>Channel 1</MenuItem>
                        <MenuItem value={2}>Channel 2</MenuItem>
                        <MenuItem value={3}>Channel 3</MenuItem>
                        <MenuItem value={4}>Channel 4</MenuItem>
                      </Select>
                    </FormControl>
                  </Stack>
                </Grid>
              </Grid>
            </DialogContent>
          <DialogActions>
            <Button onClick={() => this.setState({ window: '' })}>{ETVConstants.trans('close') || 'close'}</Button>
            <Button
              disabled={!canSaveSatisfaction}
              onClick={this.saveSurveyWithImage}
              startIcon={<SaveIcon />}
              variant="contained"
              color="success"
            >
              {ETVConstants.trans('save') || 'save'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Create General Survey */}
        <Dialog
          open={this.state.window === 'create_general_survey_div'}
          keepMounted
          maxWidth="xl"
          fullWidth
          onClose={() => {}}
          PaperProps={{ sx: { borderRadius: 3 } }}
        >
          <DialogTitle>{ETVConstants.trans('create_general_survey') || 'create general survey'}</DialogTitle>
          <DialogContent dividers>
            <Grid container spacing={3} sx={{ mt: 0.5 }}>
              {/* left: 2/3 image */}
              <Grid item xs={12} md={8}>
                {this.renderImageEditorBlock()}
              </Grid>

              {/* right: 1/3 questions */}
              <Grid item xs={12} md={4}>
                <Stack spacing={2}>
                  <TextField
                    required
                    label={ETVConstants.trans('survey_question') || 'survey question'}
                    fullWidth
                    value={this.state.new_question}
                    onChange={(e) => this.setState({ new_question: e.target.value })}
                  />

                  <TextField
                    required
                    label={ETVConstants.trans('answer') || 'answer'}
                    fullWidth
                    value={this.state.ans_1}
                    onChange={(e) => this.setState({ ans_1: e.target.value })}
                  />
                  <TextField
                    required
                    label={ETVConstants.trans('answer') || 'answer'}
                    fullWidth
                    value={this.state.ans_2}
                    onChange={(e) => this.setState({ ans_2: e.target.value })}
                  />
                  <TextField
                    label={ETVConstants.trans('answer') || 'answer'}
                    fullWidth
                    value={this.state.ans_3}
                    onChange={(e) => this.setState({ ans_3: e.target.value })}
                  />
                  <TextField
                    label={ETVConstants.trans('answer') || 'answer'}
                    fullWidth
                    value={this.state.ans_4}
                    onChange={(e) => this.setState({ ans_4: e.target.value })}
                  />
                  <TextField
                    label={ETVConstants.trans('answer') || 'answer'}
                    fullWidth
                    value={this.state.ans_5}
                    onChange={(e) => this.setState({ ans_5: e.target.value })}
                  />

                  <Button variant="contained" onClick={() => this.showWindow('select_screens_div', true)}>
                    {ETVConstants.trans('select_screens') || 'select screens'} ({this.state.selected_screens?.length || 0})
                  </Button>

                  <FormControl sx={{ mt: 1, width: '100%' }}>
                    <InputLabel>{ETVConstants.trans('channel') || 'channel'}</InputLabel>
                    <Select
                      label={ETVConstants.trans('channel') || 'channel'}
                      value={this.state.selected_channel}
                      onChange={(e) => this.setState({ selected_channel: e.target.value })}
                    >
                      <MenuItem value={0}>Channel 0</MenuItem>
                      <MenuItem value={1}>Channel 1</MenuItem>
                      <MenuItem value={2}>Channel 2</MenuItem>
                      <MenuItem value={3}>Channel 3</MenuItem>
                      <MenuItem value={4}>Channel 4</MenuItem>
                    </Select>
                  </FormControl>
                </Stack>
              </Grid>
            </Grid>
          </DialogContent>

          <DialogActions>
            <Button onClick={() => this.setState({ window: '' })}>{ETVConstants.trans('close') || 'close'}</Button>
            <Button
              disabled={!canSaveGeneral}
              onClick={this.saveSurveyWithImage}
              startIcon={<SaveIcon />}
              variant="contained"
              color="success"
            >
              {ETVConstants.trans('save') || 'save'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Screen selection overlay */}
        <div id="select_screens_div" style={{ display: this.state.window === 'select_screens_div' ? 'block' : 'none' }}>
          <SelectAvailableScreensNew
            isOpen={this.state.window === 'select_screens_div'}
            user_id={this.props.user_id}
            closeHook={this.closeScreenSelection}
            selectedScreens={this.selectedScreens}
            selected={this.state.selected_screens || []}
          />
        </div>

        {/* Analyze dialog */}
        <Dialog
          open={this.state.window === 'analyze_survey_div'}
          maxWidth="md"
          fullWidth
          onClose={() => this.setState({ window: '' })}
          PaperProps={{ sx: { borderRadius: 3 } }}
        >
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <BarChartIcon />
            <span>{ETVConstants.trans('analysis') || 'analysis'}</span>
          </DialogTitle>

          <DialogContent dividers>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
              {this.state.question}
            </Typography>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center" sx={{ mb: 2 }}>
              {this.createLanguageDropDown()}
            </Stack>

            {this.state.data ? (
              <Box ref={this.analysisRef}>
                <Stack direction="row" spacing={1.5} sx={{ mb: 2, flexWrap: 'wrap' }}>
                  <Chip label={`total hits: ${totalHits}`} color="primary" variant="outlined" />
                  {this.getLeader() ? (
                    <Chip
                      label={`top answer: ${this.getLeader().name} (${this.getLeader().hits})`}
                      color="success"
                      variant="outlined"
                    />
                  ) : null}
                </Stack>

                {totalHits === 0 ? (
                  <Stack spacing={2} sx={{ mt: 1 }}>
                    <Box
                      sx={{
                        p: 3,
                        borderRadius: 2,
                        border: (t) => `1px dashed ${t.palette.divider}`,
                        textAlign: 'center',
                        bgcolor: 'background.default',
                      }}
                    >
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
                        {ETVConstants.trans('no_responses_yet') || 'no responses yet'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {ETVConstants.trans('survey_waiting_for_votes') ||
                          'this survey is waiting for votes. as soon as results come in, charts will appear here.'}
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        height: 300,
                        width: '100%',
                        bgcolor: 'background.paper',
                        borderRadius: 2,
                        border: (t) => `1px solid ${t.palette.divider}`,
                      }}
                    >
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={chartRowsEps} layout="vertical" margin={{ top: 16, right: 24, left: 8, bottom: 16 }}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis type="number" domain={[0, 1]} allowDecimals={false} />
                          <YAxis type="category" dataKey="name" width={140} />
                          <ReTooltip formatter={(v) => [v, ETVConstants.trans('hits')]} labelFormatter={(l) => `${l}`} />
                          <Bar dataKey="hits" radius={[6, 6, 6, 6]}>
                            {chartRowsEps.map((_, idx) => (
                              <Cell key={`bar0-${idx}`} fill={COLORS[idx % COLORS.length]} />
                            ))}
                            <LabelList dataKey="hits" position="right" style={{ fontWeight: 600 }} />
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </Box>
                  </Stack>
                ) : this.state.chart_mode === 'bar' ? (
                  <Box
                    sx={{
                      height: 420,
                      width: '100%',
                      bgcolor: 'background.paper',
                      borderRadius: 2,
                      border: (t) => `1px solid ${t.palette.divider}`,
                    }}
                  >
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={chartRowsEps} layout="vertical" margin={{ top: 16, right: 24, left: 8, bottom: 16 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis type="number" domain={[0, Math.max(1, dataMax)]} allowDecimals={false} />
                        <YAxis type="category" dataKey="name" width={140} />
                        <ReTooltip formatter={(v) => [v, ETVConstants.trans('hits')]} labelFormatter={(l) => `${l}`} />
                        <Bar dataKey="hits" radius={[6, 6, 6, 6]}>
                          {chartRowsEps.map((_, idx) => (
                            <Cell key={`bar-${idx}`} fill={COLORS[idx % COLORS.length]} />
                          ))}
                          <LabelList dataKey="hits" position="right" style={{ fontWeight: 600 }} />
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </Box>
                ) : (
                  <Box
                    sx={{
                      height: 420,
                      width: '100%',
                      bgcolor: 'background.paper',
                      borderRadius: 2,
                      border: (t) => `1px solid ${t.palette.divider}`,
                    }}
                  >
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie data={chartRows} dataKey="hits" nameKey="name" innerRadius="55%" outerRadius="85%" paddingAngle={2}>
                          {chartRows.map((_, idx) => (
                            <Cell key={`slice-${idx}`} fill={COLORS[idx % COLORS.length]} />
                          ))}
                          <LabelList dataKey="hits" position="outside" style={{ fontWeight: 600 }} formatter={(v) => `${v}`} />
                        </Pie>
                        <Legend />
                        <ReTooltip
                          formatter={(v) => [
                            `${v} (${Math.round((v / Math.max(1, totalHits)) * 100)}%)`,
                            ETVConstants.trans('hits'),
                          ]}
                          labelFormatter={(l) => `${l}`}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </Box>
                )}
              </Box>
            ) : (
              <Box sx={{ py: 4, textAlign: 'center' }}>{ETVConstants.trans('loading_chart') || 'loading chart'}</Box>
            )}
          </DialogContent>

          <DialogActions sx={{ gap: 1 }}>
            <Tooltip
              title={
                this.state.chart_mode === 'bar'
                  ? ETVConstants.trans('switch_to_donut') || 'switch to donut'
                  : ETVConstants.trans('switch_to_bar') || 'switch to bar'
              }
            >
              <Button
                variant="outlined"
                startIcon={this.state.chart_mode === 'bar' ? <DonutLargeIcon /> : <StackedBarChartIcon />}
                onClick={() => this.toggleChartMode()}
                disabled={totalHits === 0}
              >
                {this.state.chart_mode === 'bar' ? ETVConstants.trans('donut') || 'donut' : ETVConstants.trans('bar') || 'bar'}
              </Button>
            </Tooltip>

            <Tooltip title={ETVConstants.trans('export_png') || 'export png'}>
              <Button variant="outlined" startIcon={<IosShareIcon />} onClick={() => this.exportAnalysisAsPNG()}>
                PNG
              </Button>
            </Tooltip>

            <Tooltip title={ETVConstants.trans('export_pdf') || 'export pdf'}>
              <Button variant="outlined" startIcon={<PictureAsPdfIcon />} onClick={() => this.exportAnalysisAsPDF()}>
                PDF
              </Button>
            </Tooltip>

            <Box flexGrow={1} />
            <Button onClick={() => this.setState({ window: '' })}>{ETVConstants.trans('close') || 'close'}</Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }
}

export default SurveyMgmt;
